import { useEffect, useRef, useState } from 'react';
import { bt } from '../../i18n';

/**
 * A row in a catalog picker — a product or a collection. `thumbnailUrl` drives the small cover; `meta`
 * is an optional secondary line (e.g. "12 products").
 */
export interface PickerItem {
  id: string;
  name: string;
  thumbnailUrl?: string | null;
  meta?: string | null;
}

/**
 * A stable data source for {@link EntityPickerListbox}: `search(term)` returns a page of candidates
 * (server-side search — never a client filter of one big page, which would hide older entities), and
 * `resolveOne(id)` fetches the single currently-selected entity so the trigger can show its name even
 * when it is not on the current search page. MUST be a MODULE-LEVEL constant (stable identity) so the
 * effects below don't re-run every render.
 */
export interface PickerSource {
  search: (term: string) => Promise<PickerItem[]>;
  resolveOne: (id: string) => Promise<PickerItem | null>;
  /**
   * SYNCHRONOUS best-effort lookup — whatever is already known about `id` with no request and no await.
   * Optional; when a source provides it, the trigger names the bound entity on the FIRST frame instead of
   * printing "…" until a list request lands (founder report 2026-09-17: opening the builder on a section
   * bound to a real product showed "…" over a sample product). Never authoritative: `resolveOne` still runs
   * and corrects the label.
   */
  peek?: (id: string) => PickerItem | null;
}

/**
 * Searchable catalog listbox — the shared engine behind `ProductControl` / `CollectionControl` (feature
 * 0023 P1). Models the `MenuControl` recipe (custom in-app listbox, never a native `<select>`; lazy fetch
 * on first open) but adds a debounced SERVER search + thumbnail rows, because a real catalog has far more
 * entries than a store's menus.
 *
 * SELF-CANCELLING-EFFECT SAFETY (the `MenuControl` "Loading…" gotcha): `loading` is NEVER in an effect's
 * deps or guard — including it would make `setLoading(true)` re-run the fetch effect, whose cleanup flips
 * the in-flight run's `alive` to false, so the settled fetch never clears `loading` and the picker sticks
 * on "Loading…" forever. Each `setItems`/`setCurrent` is paired with its `setLoading(false)` in the SAME
 * callback so React batches them into one render while `alive` is still true (no split `.finally`).
 */
export function EntityPickerListbox({
  value,
  onSelect,
  source,
  label,
  noneLabel,
  searchPlaceholder,
  emptyLabel,
  errorLabel,
}: {
  value: string | undefined;
  onSelect: (id: string | undefined) => void;
  source: PickerSource;
  label: string;
  noneLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
  errorLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState<PickerItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Seeded from the source's synchronous cache so a bound picker opens ALREADY naming its entity.
  const [current, setCurrent] = useState<PickerItem | null>(() =>
    value ? (source.peek?.(value) ?? null) : null,
  );
  // Tracks whether the async resolution for `value` has SETTLED, so the trigger can tell "still loading"
  // (→ "…") from "the server does not know this id" (→ the error label). It used to print "…" forever.
  const [resolveFailed, setResolveFailed] = useState(false);
  const currentRef = useRef<PickerItem | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Debounce the search term (250ms) so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  // Resolve the CURRENT selection's name for the trigger. Skipped when it is already known (e.g. the
  // seller just chose it, or it is unchanged), so choosing never triggers a redundant fetch.
  useEffect(() => {
    if (!value) {
      currentRef.current = null;
      setCurrent(null);
      setResolveFailed(false);
      return;
    }
    if (currentRef.current?.id === value) return;
    let alive = true;
    setResolveFailed(false);
    // Paint what the cache knows straight away; the fetch below only ever refines it.
    const peeked = source.peek?.(value) ?? null;
    if (peeked) setCurrent(peeked);
    source
      .resolveOne(value)
      .then((item) => {
        if (!alive) return;
        currentRef.current = item;
        setCurrent(item);
        setResolveFailed(item == null);
      })
      .catch(() => {
        if (alive) setResolveFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [value, source]);

  // Fetch the candidate list on open and on every (debounced) search change while open. Re-runs on term
  // change; the previous run's cleanup flips its `alive` so only the latest result lands.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setError(false);
    source
      .search(debounced)
      .then((list) => {
        if (alive) {
          setItems(list);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [open, debounced, source]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // A bound picker names its entity; "…" is ONLY the brief window before the first answer, and a failed /
  // unknown id says so instead of sitting on an ellipsis forever.
  const currentLabel = !value ? noneLabel : (current?.name ?? (resolveFailed ? errorLabel : '…'));

  const choose = (item: PickerItem) => {
    currentRef.current = item;
    setCurrent(item);
    setResolveFailed(false);
    onSelect(item.id);
    setOpen(false);
  };
  const chooseNone = () => {
    currentRef.current = null;
    setCurrent(null);
    setResolveFailed(false);
    onSelect(undefined);
    setOpen(false);
  };

  return (
    <div className="bld-listbox bld-picker" ref={ref}>
      <button
        type="button"
        className="bld-listbox__btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      >
        <span className="bld-listbox__val">{currentLabel}</span>
        <svg
          className="bld-listbox__caret"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 6.5L8 3.5L11 6.5M5 9.5L8 12.5L11 9.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="bld-picker__pop" role="dialog" aria-label={label}>
          <input
            type="text"
            className="bld-input bld-picker__search"
            placeholder={searchPlaceholder}
            value={term}
            autoFocus
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
          />
          <ul className="bld-listbox__menu bld-picker__menu" role="listbox" aria-label={label}>
            <li
              role="option"
              aria-selected={!value}
              className="bld-listbox__opt bld-picker__opt bld-picker__opt--none"
              onClick={chooseNone}
            >
              {noneLabel}
            </li>
            {/* Only show "Loading…" on the FIRST-ever fetch (no items yet). A cached reopen or a
                background re-search keeps the current list visible so it feels instant instead of
                flashing the spinner. */}
            {loading && !items && (
              <li className="bld-listbox__opt bld-listbox__opt--hint">
                {bt('chrome.common.loading_cap', 'Loading…')}
              </li>
            )}
            {error && <li className="bld-listbox__opt bld-listbox__opt--hint">{errorLabel}</li>}
            {!loading && !error && items?.length === 0 && (
              <li className="bld-listbox__opt bld-listbox__opt--hint">{emptyLabel}</li>
            )}
            {!error &&
              items?.map((item) => (
                <li
                  key={item.id}
                  role="option"
                  aria-selected={item.id === value}
                  className="bld-listbox__opt bld-picker__opt"
                  onClick={() => choose(item)}
                >
                  <span className="bld-picker__thumb" aria-hidden="true">
                    {item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt="" loading="lazy" />
                    ) : (
                      <span className="bld-picker__thumb--empty" />
                    )}
                  </span>
                  <span className="bld-picker__text">
                    <span className="bld-picker__name">{item.name}</span>
                    {item.meta && <span className="bld-picker__meta">{item.meta}</span>}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
